import axios from 'axios';

// API Base URL - automatically switches between development and production
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? `${window.location.origin}/api`  // Production: uses same domain
  : 'http://localhost:3005/api';     // Development: uses localhost:3005

console.log('API Base URL:', API_BASE_URL);

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Add response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is a token expiration (not a form validation error)
      const errorMessage = error.response?.data?.message;
      const isTokenError = errorMessage?.includes('Token') || 
                          errorMessage?.includes('token') || 
                          errorMessage?.includes('expired') ||
                          errorMessage?.includes('invalid') ||
                          errorMessage === 'Access denied';
      
      // Only redirect if it's actually an auth token issue
      if (isTokenError) {
        const currentPath = window.location.pathname;
        
        // Only redirect if we're on a protected route
        if (currentPath.includes('/admin') || currentPath.includes('/map-admin')) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth service
export const authService = {
  login: async (username, password) => {
    const response = await api.post('/auth/login', { username, password });
    const { token, appToken } = response.data;
    localStorage.setItem('token', token);
    // A linked app_users identity, purely so this staff account has
    // somewhere to save lands too — app_saved_lands is now the only
    // saved-lands storage, shared with consumer accounts (see
    // backend/utils/appUserLinking.js). Stored under the same key
    // consumerAuthService/appApi use, independent of the staff token.
    if (appToken) localStorage.setItem('consumerToken', appToken);
    return response.data;
  },

  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('consumerToken');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('token');
  },

  getToken: () => {
    return localStorage.getItem('token');
  }
};

// Helper function to parse coordinates string to lat/lng for map display
const parseCoordinatesForMap = (coordinatesString) => {
  if (!coordinatesString || !coordinatesString.trim()) {
    return { latitude: null, longitude: null };
  }

  const coords = coordinatesString.trim();
  
  // Try DMS format first
  const dmsRegex = /(\d+)°(\d+)'([\d.]+)"([NS])\s+(\d+)°(\d+)'([\d.]+)"([EW])/;
  const dmsMatch = coords.match(dmsRegex);
  
  if (dmsMatch) {
    const [, latDeg, latMin, latSec, latDir, lngDeg, lngMin, lngSec, lngDir] = dmsMatch;
    
    let lat = parseInt(latDeg) + parseInt(latMin) / 60 + parseFloat(latSec) / 3600;
    let lng = parseInt(lngDeg) + parseInt(lngMin) / 60 + parseFloat(lngSec) / 3600;
    
    if (latDir === 'S') lat = -lat;
    if (lngDir === 'W') lng = -lng;
    
    return { latitude: lat, longitude: lng };
  }
  
  // Try decimal format
  const parts = coords.split(',');
  if (parts.length === 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    
    if (!isNaN(lat) && !isNaN(lng)) {
      return { latitude: lat, longitude: lng };
    }
  }
  
  return { latitude: null, longitude: null };
};

// Lands service
export const landsService = {
  // Get all lands (public). { limit, offset, search, landType } is
  // opt-in (see backend's getLands) — omitted, this returns every land as
  // a bare array exactly like it always has (the public map needs the
  // full list client-side for its own tag-based filtering); passed, the
  // backend paginates/searches server-side and this returns
  // { lands, total } instead. Not currently used by the admin panel's
  // Lands tab — see the comment on its fetchLands for why.
  //
  // adminScope sends ?scope=admin, the one way to get sold/stopped/private
  // lands back (backend/utils/landAvailability.js) — for the admin panel's
  // own management views only, never a browsing surface, and it needs a
  // staff token to have any effect.
  getAllLands: async ({ limit, offset, search, landType, adminScope } = {}) => {
    const params = {
      ...(limit ? { limit, offset, search: search || undefined, land_type: landType } : {}),
      ...(adminScope ? { scope: 'admin' } : {}),
    };
    const response = await api.get('/lands', {
      params: Object.keys(params).length ? params : undefined,
    });

    const withParsedCoords = (land) => {
      const { latitude, longitude } = parseCoordinatesForMap(land.coordinates);
      return { ...land, latitude, longitude };
    };

    if (limit) {
      return { lands: response.data.lands.map(withParsedCoords), total: response.data.total };
    }
    return response.data.map(withParsedCoords);
  },

  // Get single land (public). adminScope as above — the edit form has to be
  // able to open a land that's already been sold/stopped.
  getLand: async (id, { adminScope } = {}) => {
    const response = await api.get(`/lands/${id}`, {
      params: adminScope ? { scope: 'admin' } : undefined,
    });
    // Parse coordinates for map compatibility
    const { latitude, longitude } = parseCoordinatesForMap(response.data.coordinates);
    return {
      ...response.data,
      latitude,
      longitude
    };
  },

  // Create new land (admin only)
  createLand: async (landData) => {
    // landData is already a FormData object from the frontend
    const response = await api.post('/lands', landData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Update land (admin only)
  updateLand: async (id, landData) => {
    // landData is already a FormData object from the frontend
    const response = await api.put(`/lands/${id}`, landData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete land (admin only)
  deleteLand: async (id) => {
    const response = await api.delete(`/lands/${id}`);
    return response.data;
  },

  // Tag-only update (admin only) — used by the lands table's inline
  // AI-suggest-tags action, which has no reason to touch anything else.
  updateLandTags: async (id, tagIds) => {
    const response = await api.put(`/lands/${id}/tags`, { tag_ids: tagIds });
    return response.data;
  },

  // Upload additional pictures for a land (admin only)
  uploadAdditionalPictures: async (landId, picturesData) => {
    const response = await api.post(`/lands/${landId}/pictures`, picturesData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Delete specific additional picture (admin only)
  deleteAdditionalPicture: async (landId, pictureIndex) => {
    const response = await api.delete(`/lands/${landId}/pictures/${pictureIndex}`);
    return response.data;
  },

  // Delete additional image by URL (admin only)
  deleteAdditionalImage: async (landId, imageUrl) => {
    const response = await api.delete(`/lands/${landId}/additional-image`, {
      data: { imageUrl }
    });
    return response.data;
  },

  // Delete main image (admin only)
  deleteMainImage: async (landId) => {
    const response = await api.delete(`/lands/${landId}/main-image`);
    return response.data;
  },

  // Download all pictures as ZIP
  downloadPictures: async (landId) => {
    const response = await api.get(`/lands/${landId}/pictures/download`, {
      responseType: 'blob'
    });
    return response;
  }
};

// Tags service
export const tagsService = {
  // Get all tags (public)
  getAllTags: async () => {
    const response = await api.get('/tags');
    return response.data;
  },

  // Create new tag (admin only) — tagData is a FormData object (title, color, bg_image?)
  createTag: async (tagData) => {
    const response = await api.post('/tags', tagData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Update tag (admin only) — tagData is a FormData object (title, color, bg_image?)
  updateTag: async (id, tagData) => {
    const response = await api.put(`/tags/${id}`, tagData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Delete tag (admin only)
  deleteTag: async (id) => {
    const response = await api.delete(`/tags/${id}`);
    return response.data;
  }
};

// Nested tags service — global tag hierarchy (a tag can have one parent)
export const nestedTagsService = {
  // Get all parent/child tag relationships (public)
  getAll: async () => {
    const response = await api.get('/nested-tags');
    return response.data;
  },

  // Set (or replace) a tag's parent (admin only)
  setParent: async (parentTagId, childTagId) => {
    const response = await api.post('/nested-tags', { parent_tag_id: parentTagId, child_tag_id: childTagId });
    return response.data;
  },

  // Remove a tag's nesting — it goes back to showing flat (admin only)
  removeParent: async (childTagId) => {
    const response = await api.delete(`/nested-tags/${childTagId}`);
    return response.data;
  }
};

// Agents service for WhatsApp functionality (legacy - uses users endpoint)
export const agentsService = {
  // Get all agents (public)
  getAllAgents: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  // Create new agent (admin only)
  createAgent: async (agentData) => {
    const response = await api.post('/users', agentData);
    return response.data;
  },

  // Update agent (admin only)
  updateAgent: async (id, agentData) => {
    const response = await api.put(`/users/${id}`, agentData);
    return response.data;
  },

  // Delete agent (admin only)
  deleteAgent: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

// Users service
export const usersService = {
  // Get all users (admin only)
  getUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  // Get all users (alias for compatibility)
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  // Create new user (admin only)
  createUser: async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
  },

  // Update user (admin only)
  updateUser: async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
  },

  // Delete user (admin only)
  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  }
};

// Consumer/mobile-app accounts (app_users) — read-only listing for the
// admin panel's "Users" tab, distinct from usersService's staff accounts.
export const appUsersService = {
  getAllAppUsers: async () => {
    const response = await api.get('/app-users');
    return response.data;
  },
  // Staff-only edit of name/phone/notes — every other field on this account
  // (email, google_id, apple_id, picture, push_token) is owned by the
  // mobile app's own sign-in flow and isn't touched here.
  updateAppUser: async (id, { name, phone, notes }) => {
    const response = await api.put(`/app-users/${id}`, { name, phone, notes });
    return response.data;
  }
};

// Public "add your land" submissions review queue (admin only)
export const landSubmissionsService = {
  getPending: async () => {
    const response = await api.get('/land-submissions', { params: { status: 'pending' } });
    return response.data;
  },
  markConverted: async (id, landId) => {
    const response = await api.put(`/land-submissions/${id}`, { status: 'converted', land_id: landId });
    return response.data;
  },
  reject: async (id) => {
    const response = await api.put(`/land-submissions/${id}`, { status: 'rejected' });
    return response.data;
  },
};

// Buyer-side demand requests — a consumer marks a location on the mobile
// app's map where they want land; staff follows up via WhatsApp.
export const landDemandsService = {
  getAll: async () => {
    const response = await api.get('/land-demands');
    return response.data;
  },
  update: async (id, agentNotes) => {
    const response = await api.put(`/land-demands/${id}`, { agent_notes: agentNotes });
    return response.data;
  },
  updateStatus: async (id, status) => {
    const response = await api.put(`/land-demands/${id}/status`, { status });
    return response.data;
  },
  // No staffId: self-assign (claim from the unassigned pool). With staffId
  // (admin only): reassign directly to that staff member.
  assign: async (id, staffId) => {
    const response = await api.put(`/land-demands/${id}/assign`, staffId ? { staff_id: staffId } : {});
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/land-demands/${id}`);
    return response.data;
  },
};

// Small admin-controlled runtime flags — currently just the Android map
// engine kill switch (native react-native-maps vs the mobile app's
// WebView+Leaflet fallback). See backend/controllers/appSettingsController.js.
export const appSettingsService = {
  getAll: async () => {
    const response = await api.get('/app-settings');
    return response.data;
  },
  update: async (key, value) => {
    const response = await api.put(`/app-settings/${key}`, { value });
    return response.data;
  },
};

// Orders service for managing customer inquiries
export const ordersService = {
  // Get all orders (admin only)
  getAllOrders: async () => {
    const response = await api.get('/orders');
    return response.data;
  },

  // Create new order (admin only)
  createOrder: async (orderData) => {
    const response = await api.post('/orders', orderData);
    return response.data;
  },

  // Update order (admin only)
  updateOrder: async (id, orderData) => {
    const response = await api.put(`/orders/${id}`, orderData);
    return response.data;
  },

  // Delete order (admin only)
  deleteOrder: async (id) => {
    const response = await api.delete(`/orders/${id}`);
    return response.data;
  }
};

// Natural-language search — sends the query plus the already-loaded tags
// list, gets back structured filter criteria (tag ids, price range,
// leftover keywords) to apply against the lands already in memory.
export const aiSearchService = {
  parse: async (query, tags) => {
    const response = await api.post('/ai-search', { query, tags });
    return response.data;
  },
  // Admin-only: proposes which of the existing tags apply to a land based
  // on its notes — a suggestion only, staff still approves/adjusts and
  // saves via landsService.updateLandTags.
  suggestTags: async (notes, tags) => {
    const response = await api.post('/ai-search/suggest-tags', { notes, tags });
    return response.data;
  },
};

// Staff side of the in-app agent chat (mobile's replacement for the old
// WhatsApp button) — one inbox row per app user, each with their own thread.
export const chatService = {
  getConversations: async () => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },
  // { limit, before } is opt-in (see chatController.getConversationMessages)
  // — omitted, this returns the full thread as a bare array like it always
  // has; passed, the backend returns { messages, has_more } instead, which
  // this normalizes back to the same { messages, hasMore } shape either
  // way so callers don't need to branch on whether they paginated.
  getMessages: async (appUserId, { limit, before } = {}) => {
    const response = await api.get(`/chat/conversations/${appUserId}/messages`, {
      params: limit ? { limit, before } : undefined,
    });
    return limit
      ? { messages: response.data.messages, hasMore: response.data.has_more }
      : { messages: response.data, hasMore: false };
  },
  // payload: { text?, voice_url?, voice_duration_ms?, photo_urls?, land_ids? }
  // — same shape the mobile consumer side sends, so staff can reply with
  // attachments too, not just text.
  sendMessage: async (appUserId, payload) => {
    const response = await api.post(`/chat/conversations/${appUserId}/messages`, payload);
    return response.data;
  },
  // Gives up an active claim early instead of waiting on its 24h auto-expiry
  // (see chatController.getActiveClaim) — e.g. handing a client off at the
  // end of a shift. Own claim only, unless the caller is an admin.
  releaseConversation: async (appUserId) => {
    const response = await api.post(`/chat/conversations/${appUserId}/release`);
    return response.data;
  },
  // Admin-only: assigns/reassigns a conversation directly to a chosen
  // staff member, bypassing the implicit claim-on-reply flow.
  assignConversation: async (appUserId, staffId) => {
    const response = await api.post(`/chat/conversations/${appUserId}/assign`, { staff_id: staffId });
    return response.data;
  },
  // Admin-only, destructive and unrecoverable — permanently deletes a
  // conversation's entire message history plus its claim record.
  deleteConversation: async (appUserId) => {
    const response = await api.delete(`/chat/conversations/${appUserId}`);
    return response.data;
  },
  // photos: array of File objects from an <input type="file">.
  // voiceBlob: a recorded Blob from MediaRecorder, or undefined.
  uploadMedia: async ({ photos = [], voiceBlob } = {}) => {
    const form = new FormData();
    photos.forEach((file, i) => form.append('photos', file, file.name || `photo-${i}.jpg`));
    if (voiceBlob) form.append('voice', voiceBlob, 'voice.webm');
    const response = await api.post('/chat/staff-upload', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },
  // Saved broadcast audiences ("groups") — see chatGroupsController for the
  // fan-out-to-1:1-threads bulk-send model.
  getGroups: async () => {
    const response = await api.get('/chat/groups');
    return response.data;
  },
  getGroup: async (groupId) => {
    const response = await api.get(`/chat/groups/${groupId}`);
    return response.data;
  },
  createGroup: async ({ title, app_user_ids }) => {
    const response = await api.post('/chat/groups', { title, app_user_ids });
    return response.data;
  },
  updateGroup: async (groupId, { title, app_user_ids }) => {
    const response = await api.put(`/chat/groups/${groupId}`, { title, app_user_ids });
    return response.data;
  },
  deleteGroup: async (groupId) => {
    const response = await api.delete(`/chat/groups/${groupId}`);
    return response.data;
  },
  // payload: { text?, voice_url?, photo_urls?, land_ids? } — same shape as
  // a single reply, fanned out to every member's own thread server-side.
  sendGroupMessage: async (groupId, payload) => {
    const response = await api.post(`/chat/groups/${groupId}/messages`, payload);
    return response.data;
  },
};

// Guideline 1.2 (User-Generated Content) moderation queue — reported
// comments and recent block events, so staff can act within 24 hours.
export const moderationService = {
  getReports: async (onlyUnresolved = true) => {
    const response = await api.get('/moderation/reports', { params: { resolved: onlyUnresolved ? 'false' : 'true' } });
    return response.data;
  },
  resolveReport: async (reportId, action, banUser = false) => {
    const response = await api.post(`/moderation/reports/${reportId}/resolve`, { action, banUser });
    return response.data;
  },
  getBlockEvents: async () => {
    const response = await api.get('/moderation/blocks');
    return response.data;
  },
  getAllComments: async () => {
    const response = await api.get('/moderation/comments');
    return response.data;
  },
  deleteComment: async (commentId) => {
    const response = await api.delete(`/moderation/comments/${commentId}`);
    return response.data;
  },
  getFriendReports: async (onlyUnresolved = true) => {
    const response = await api.get('/moderation/friend-reports', { params: { resolved: onlyUnresolved ? 'false' : 'true' } });
    return response.data;
  },
  resolveFriendReport: async (reportId, action, banUser = false) => {
    const response = await api.post(`/moderation/friend-reports/${reportId}/resolve`, { action, banUser });
    return response.data;
  },
};

// Map "services" layer (mosques, restaurants, fuel stations, etc.) — a
// simpler shape than lands, no tags/pricing/staff redaction involved.
export const servicesService = {
  getAll: async () => {
    const response = await api.get('/services');
    return response.data;
  },
  create: async ({ category, title, description, latitude, longitude, picture }) => {
    const form = new FormData();
    form.append('category', category);
    form.append('title', title);
    if (description) form.append('description', description);
    form.append('latitude', latitude);
    form.append('longitude', longitude);
    if (picture) form.append('picture', picture);
    const response = await api.post('/services', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return response.data;
  },
  remove: async (id) => {
    const response = await api.delete(`/services/${id}`);
    return response.data;
  },
};

// Staff checklist of which PACI-numbered plots have been photographed —
// see backend/controllers/shootingController.js.
export const shootingService = {
  getStatus: async (paciNumber) => {
    const response = await api.get(`/shooting/${encodeURIComponent(paciNumber)}`);
    return response.data.shooted;
  },
  setStatus: async (paciNumber, shooted) => {
    const response = await api.post('/shooting/toggle', { paci_number: paciNumber, shooted });
    return response.data;
  },
};

// Logs a staff WhatsApp share (single or bulk) as an operation on the
// sister site derwazaq8.com — see backend/controllers/derwazaController.js.
export const derwazaService = {
  logWhatsAppShare: async (phone, landIds) => {
    const response = await api.post('/derwaza/log-whatsapp-share', { phone, land_ids: landIds });
    return response.data;
  },
};

export default api;